import type { Column } from '@tanstack/react-table'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronsUpDownIcon,
  EyeOffIcon,
  RotateCcwIcon,
} from 'lucide-react'
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu'
import { cn } from '@/lib/utils'

export type DataTableColumnHeaderProps<TData, TValue> = {
  column: Column<TData, TValue>
  title: string
  className?: string
}

/**
 * Sortable/hideable column header with a readable actions menu.
 */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>): React.ReactElement {
  if (!column.getCanSort() && !column.getCanHide()) {
    return (
      <div
        className={cn(
          'text-xs font-semibold tracking-wide text-muted-foreground',
          className,
        )}
      >
        {title}
      </div>
    )
  }

  const sorted = column.getIsSorted()

  return (
    <Menu>
      <MenuTrigger
        className={cn(
          '-ms-1.5 flex h-8 max-w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold tracking-wide text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-popup-open:bg-accent data-popup-open:text-foreground [&_svg]:size-3.5 [&_svg]:shrink-0',
          sorted && 'text-foreground',
          className,
        )}
        aria-label={`${title} column actions`}
      >
        <span className="truncate">{title}</span>
        {column.getCanSort() ? (
          sorted === 'desc' ? (
            <ArrowDownIcon className="text-foreground" />
          ) : sorted === 'asc' ? (
            <ArrowUpIcon className="text-foreground" />
          ) : (
            <ChevronsUpDownIcon className="opacity-40" />
          )
        ) : null}
      </MenuTrigger>
      <MenuPopup align="start" className="min-w-44">
        {column.getCanSort() ? (
          <>
            <MenuItem
              onClick={() => column.toggleSorting(false)}
              className={cn(sorted === 'asc' && 'bg-accent')}
            >
              <ArrowUpIcon />
              Sort ascending
            </MenuItem>
            <MenuItem
              onClick={() => column.toggleSorting(true)}
              className={cn(sorted === 'desc' && 'bg-accent')}
            >
              <ArrowDownIcon />
              Sort descending
            </MenuItem>
            {sorted ? (
              <MenuItem onClick={() => column.clearSorting()}>
                <RotateCcwIcon />
                Clear sort
              </MenuItem>
            ) : null}
            {column.getCanHide() ? <MenuSeparator /> : null}
          </>
        ) : null}
        {column.getCanHide() ? (
          <MenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeOffIcon />
            Hide column
          </MenuItem>
        ) : null}
      </MenuPopup>
    </Menu>
  )
}
